<template>
  <div
    class="absolute top-0 left-0 flex flex-col w-full h-full overflow-hidden rounded-lg gap-y-4"
  >
    <div
      class="relative flex flex-col w-full h-full p-5 bg-transparent border rounded-lg sm:p-2 gap-y-5"
    >
      <div class="absolute top-0 left-0 flex flex-col w-full h-full gap-4 p-4">
        <div class="flex items-center justify-between">
          <h2 class="mb-4 text-xl text-eblack-950">{{ $t("settings.auto.configuracion_general") }}</h2>
          <div class="flex items-end px-4 mt-auto">
            <button
              type="button"
              class="px-4 py-2 mb-2 text-sm text-white transition-colors duration-200 rounded-md"
              :class="{
                'bg-green-500 hover:bg-green-600':
                  !isOperationInProgress && isFormValid,
                'bg-gray-400 cursor-not-allowed':
                  isOperationInProgress || !isFormValid,
              }"
              :disabled="isOperationInProgress || !isFormValid"
              @click="updateSystemConfig"
            >
              <span v-if="isSaving" class="flex items-center">
                <svg
                  class="w-4 h-4 mr-2 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="m12 2 8 4-8 4-8-4 8-4z"
                  ></path>
                </svg>{{ $t("settings.auto.guardando") }}</span>
              <span v-else>{{ $t("settings.auto.guardar") }}</span>
            </button>
          </div>
        </div>
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center p-8">
          <div class="flex items-center text-gray-600">
            <svg
              class="w-5 h-5 mr-2 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="m12 2 8 4-8 4-8-4 8-4z"
              ></path>
            </svg>{{ $t("settings.auto.cargando_configuracion") }}</div>
        </div>

        <!-- Content -->
        <div v-else class="flex-1 flex flex-col gap-y-6 overflow-y-auto">
          <div class="flex flex-col gap-y-2">
            <label for="itinerary_expiration_time" class="text-sm"
              >{{ $t("settings.auto.tiempo_de_expiracion_de_itinerarios_horas") }}</label
            >
            <input
              id="itinerary_expiration_time"
              type="number"
              name="itinerary_expiration_time"
              class="w-full max-w-xs px-3 py-2 text-sm transition-colors border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              placeholder="0"
              min="0"
              step="1"
              v-model.number="systemConfig.itinerary_expiration_time"
              @blur="validateNumberInput"
              :disabled="isOperationInProgress"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label for="emanta_commission_percentage" class="text-sm">{{ $t("settings.auto.porcentaje_de_comision_de_emanta") }}</label>

            <input
              id="emanta_commission_percentage"
              type="number"
              name="emanta_commission_percentage"
              class="w-full max-w-xs px-3 py-2 text-sm transition-colors border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              placeholder="0"
              min="0"
              step="1"
              v-model.number="systemConfig.emanta_commission_percentage"
              @blur="validateNumberInput"
              :disabled="isOperationInProgress"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label for="emanta_commission_iva_rate" class="text-sm">{{ $t("settings.auto.iva_sobre_comision_de_emanta") }}</label>
            <input
              id="emanta_commission_iva_rate"
              type="number"
              name="emanta_commission_iva_rate"
              class="w-full max-w-xs px-3 py-2 text-sm transition-colors border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              min="0"
              max="1"
              step="0.01"
              v-model.number="systemConfig.emanta_commission_iva_rate"
              :disabled="isOperationInProgress"
            />
          </div>

          <label class="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              v-model="systemConfig.charge_percentage_compounding_enabled"
              :disabled="isOperationInProgress"
            />
            {{ $t("settings.auto.aplicar_cargos_porcentuales_de_forma_compuesta") }}
          </label>

          <label class="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              v-model="systemConfig.customer_iva_enabled"
              :disabled="isOperationInProgress"
            />
            {{ $t("settings.auto.cobrar_iva_al_cliente") }}
          </label>

          <div class="flex flex-col gap-y-2">
            <label for="customer_iva_rate" class="text-sm">{{ $t("settings.auto.iva_al_cliente") }}</label>
            <input
              id="customer_iva_rate"
              type="number"
              name="customer_iva_rate"
              class="w-full max-w-xs px-3 py-2 text-sm transition-colors border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              min="0"
              max="1"
              step="0.01"
              v-model.number="systemConfig.customer_iva_rate"
              :disabled="isOperationInProgress || !systemConfig.customer_iva_enabled"
            />
          </div>

          <FeatureFlagAdminPanel />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import { useToast } from "vue-toastification";
import systemRequest from "@/services/admin/system-service";
import { FeatureFlagAdminPanel } from "@/domains/feature-flags";

export default {
  name: "ConfigurationsIndex",
  data() {
    return {
      systemConfig: {
        itinerary_expiration_time: 0,
        emanta_commission_percentage: 0,
        charge_percentage_compounding_enabled: false,
        customer_iva_enabled: true,
        customer_iva_rate: 0.13,
        emanta_commission_iva_rate: 0.13,
      },
      isLoading: false,
      isSaving: false,
      toast: useToast(),
      configExists: false,
    };
  },
  components: {
    FeatureFlagAdminPanel,
  },
  computed: {
    isFormValid() {
      return (
        this.systemConfig.itinerary_expiration_time >= 0 &&
        this.systemConfig.customer_iva_rate >= 0 &&
        this.systemConfig.customer_iva_rate <= 1 &&
        this.systemConfig.emanta_commission_iva_rate >= 0 &&
        this.systemConfig.emanta_commission_iva_rate <= 1
      );
    },

    isOperationInProgress() {
      return this.isLoading || this.isSaving;
    },
  },
  methods: {
    initData() {
      this.getSystemConfig();
    },

    async getSystemConfig() {
      this.isLoading = true;

      try {
        const response = await systemRequest.find(1);

        if (response.status === 200 && response.data) {
          this.systemConfig = { ...response.data };
          this.configExists = true;
        } else {
          this.configExists = false;
          this.toast.info(i18n.global.t("settings.auto.no_se_encontro_configuracion_existente_se_creara")
          );
        }
      } catch (error) {
        this.configExists = false;
      } finally {
        this.isLoading = false;
      }
    },

    async updateSystemConfig() {
      if (!this.isFormValid) {
        this.toast.error(i18n.global.t("settings.auto.por_favor_ingrese_valores_validos_en_todos_los_c")
        );
        return;
      }

      this.isSaving = true;

      try {
        let response;

        if (this.configExists) {
          response = await this.updateExistingConfig();
        } else {
          response = await this.createNewConfig();
        }

        if (response && this.isSuccessResponse(response.status)) {
          this.configExists = true;
          this.toast.success(
            this.configExists
              ? "Configuración actualizada correctamente."
              : "Configuración creada correctamente."
          );
        }
      } catch (error) {
        this.handleError(
          error,
          this.configExists
            ? "Error al actualizar la configuración del sistema"
            : "Error al crear la configuración del sistema"
        );
      } finally {
        this.isSaving = false;
      }
    },

    async updateExistingConfig() {
      // Clean up the config data before sending
      const configData = { ...this.systemConfig };

      // Remove fields that shouldn't be sent in update request
      delete configData.id;
      delete configData.created_at;
      delete configData.updated_at;

      return await systemRequest.update(1, configData);
    },

    async createNewConfig() {
      const response = await systemRequest.save({ ...this.systemConfig });
      return response;
    },

    isSuccessResponse(status) {
      return status === 200 || status === 201;
    },

    handleError(error, message) {
      this.toast.error(message);
      console.error(message, error);
    },

    validateNumberInput(event) {
      const value = parseFloat(event.target.value);
      if (isNaN(value) || value < 0) {
        event.target.value = 0;
        this.systemConfig.itinerary_expiration_time = 0;
      }
    },
  },
  mounted() {
    this.initData();
  },
};
</script>
